// The blue brand slab shown beside every auth surface (via AuthShell, and
// directly by RequestAccessPage). Presentation only; only the copy varies
// between surfaces, so only copy is a prop.
import { motion, useReducedMotion } from 'framer-motion'

export default function AuthBrandPanel({ heading, subtitle, items = [] }) {
  const reduceMotion = useReducedMotion()

  return (
    // Flush to the top/left/bottom viewport edges, rounded only on the right
    // corners — a slab cut into the page. Hidden below `lg` so the form owns
    // the fold on mobile; callers use `lg:order-1` and keep the form first
    // in the DOM so tab order is unaffected.
    <div className="hidden lg:flex lg:w-[42%] shrink-0 lg:order-1">
      <div className="relative w-full overflow-hidden rounded-r-3xl rounded-l-none bg-hero-gradient">
        <div className="absolute inset-0 bg-mesh-dots opacity-[0.15]" aria-hidden="true" />

        {/* Scrim — load-bearing for WCAG AA. `hero-gradient` runs at 160deg, so
            combined with this vertical scrim and the glow blob below, the
            lightest rendered zone is top-right, not the bottom — measure there.
            At 10/20/35 the body copy is ~6.6:1 and the caption ~5.9:1. Keep
            small text on this panel at /85 opacity or above. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/35"
          aria-hidden="true"
        />

        <motion.div
          aria-hidden="true"
          className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
          animate={reduceMotion ? undefined : { y: [0, -24, 0] }}
          transition={reduceMotion ? undefined : { duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden="true"
          className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-teal-400/10 blur-3xl"
          animate={reduceMotion ? undefined : { y: [0, 20, 0] }}
          transition={reduceMotion ? undefined : { duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
        />

        <div className="relative z-10 flex flex-col h-full px-8 py-8 lg:px-10 lg:py-10">
          <span className="inline-flex w-fit rounded-2xl bg-white px-6 py-4 shadow-sm">
            <img src="/iewrs-logo.png?v=3" alt="IEWRS — Integrated Early Warning & Response System" width="1000" height="666" className="h-20 w-auto" />
          </span>

          <div className="flex flex-col justify-center flex-1 mt-10">
            <h1 className="text-2xl xl:text-3xl font-bold tracking-tight text-white text-balance">
              {heading}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-white/95 text-balance">
              {subtitle}
            </p>

            <ul className="mt-10 space-y-4">
              {items.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-start gap-3 text-sm text-white/95">
                  <span className="flex items-center justify-center w-8 h-8 rounded-2xl bg-white/10 ring-1 ring-white/15 shrink-0">
                    <Icon className="w-4 h-4 text-white" strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="pt-1.5">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/85 mt-auto pt-10">
            Ministry of Health &middot; Rwanda Biomedical Centre &middot; Rwanda Space Agency
          </p>
        </div>
      </div>
    </div>
  )
}
